// Módulo de Contabilidad (agnóstico de Zeta — sirve sin Zeta y se conecta cuando llegue API).
// Tablas: cuentas_contables, gastos_fijos_mensuales
// Pendiente fase 2: zeta_sync_log, mapeo categorías Limpack → cuentas

import { supabase } from './supabase'

// ──────────── GASTOS FIJOS MENSUALES ────────────

export async function getGastosFijos({ soloActivos = false } = {}) {
  let q = supabase.from('gastos_fijos_mensuales').select('*').order('orden').order('descripcion')
  if (soloActivos) q = q.eq('activo', true)
  const { data, error } = await q
  if (error) { console.error('getGastosFijos:', error); return [] }
  return data || []
}

export async function crearGastoFijo(g) {
  const { data, error } = await supabase.from('gastos_fijos_mensuales').insert(g).select().single()
  if (error) throw error
  return data
}

export async function actualizarGastoFijo(id, patch) {
  const { data, error } = await supabase.from('gastos_fijos_mensuales').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function borrarGastoFijo(id) {
  const { error } = await supabase.from('gastos_fijos_mensuales').delete().eq('id', id)
  if (error) throw error
}

/** Calcula totales del mes según gastos activos. */
export async function getTotalGastosFijosMes() {
  const gastos = await getGastosFijos({ soloActivos: true })
  let totalConIva = 0
  let totalCostoNeto = 0
  let totalDeducibleIrae = 0
  let totalCreditoFiscalIva = 0
  for (const g of gastos) {
    const conIva = Number(g.monto_neto) * (1 + Number(g.tasa_iva) / 100)
    const descConIva = Number(g.monto_descuento || 0) * (1 + Number(g.tasa_iva) / 100)
    const neto = conIva - descConIva
    totalConIva += conIva
    totalCostoNeto += neto
    if (g.es_deducible_irae) totalDeducibleIrae += neto
    if (g.tiene_credito_fiscal_iva) {
      totalCreditoFiscalIva += (Number(g.monto_neto) - Number(g.monto_descuento || 0)) * (Number(g.tasa_iva) / 100)
    }
  }
  return {
    cantidad: gastos.length,
    totalConIva: Math.round(totalConIva),
    totalCostoNeto: Math.round(totalCostoNeto),
    totalDeducibleIrae: Math.round(totalDeducibleIrae),
    totalCreditoFiscalIva: Math.round(totalCreditoFiscalIva),
  }
}

// ──────────── PLAN DE CUENTAS ────────────

export async function getCuentasContables({ soloActivas = false } = {}) {
  let q = supabase.from('cuentas_contables').select('*').order('codigo')
  if (soloActivas) q = q.eq('activa', true)
  const { data, error } = await q
  if (error) { console.error('getCuentasContables:', error); return [] }
  return data || []
}

export async function crearCuentaContable(c) {
  const { data, error } = await supabase.from('cuentas_contables').insert(c).select().single()
  if (error) throw error
  return data
}

export async function actualizarCuentaContable(id, patch) {
  const { data, error } = await supabase.from('cuentas_contables').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function borrarCuentaContable(id) {
  const { error } = await supabase.from('cuentas_contables').delete().eq('id', id)
  if (error) throw error
}

/** Aprobar (o reabrir) una cuenta Zeta. nuevoEstado ∈ borrador|validado_roberto|rechazado */
export async function aprobarCuentaContable(id, nuevoEstado = 'validado_roberto', comentario = null) {
  const patch = {
    estado_validacion: nuevoEstado,
    validado_por_roberto_at: nuevoEstado === 'validado_roberto' ? new Date().toISOString() : null,
  }
  if (comentario !== null) patch.roberto_comentario = comentario
  const { error } = await supabase.from('cuentas_contables').update(patch).eq('id', id)
  if (error) throw error
}

/** Aprobar masivamente un array de ids */
export async function aprobarCuentasContablesMasivo(ids) {
  if (!ids?.length) return 0
  const { error, count } = await supabase
    .from('cuentas_contables')
    .update({
      estado_validacion: 'validado_roberto',
      validado_por_roberto_at: new Date().toISOString(),
    }, { count: 'exact' })
    .in('id', ids)
  if (error) throw error
  return count || ids.length
}

// Literales tributarios (Roberto / DGI F2181)
export const LITERALES_TRIBUTARIOS = {
  501: 'PF (ventas)',
  502: 'Empresas IVA básica (ventas)',
  503: 'Empresas IVA mínima (ventas)',
  504: 'Exentos (compras)',
  505: 'IVA básica compras',
  506: 'IVA mínima compras',
}

// ──────────── TIPOS DE CAMBIO BCU ────────────

export const MONEDAS_BCU = {
  USD: 2225,
  EUR: 1111,
  BRL: 1001,
  ARS: 501,
}

export async function getTiposCambio({ desde, hasta, iso } = {}) {
  let q = supabase.from('tipos_cambio_bcu').select('*').order('fecha', { ascending: false })
  if (desde) q = q.gte('fecha', desde)
  if (hasta) q = q.lte('fecha', hasta)
  if (iso) q = q.eq('codigo_iso', iso)
  const { data, error } = await q.limit(200)
  if (error) { console.error('getTiposCambio:', error); return [] }
  return data || []
}

/** Set manual del TC via edge function (queda fuente='manual') */
export async function setTipoCambioManual({ fecha, codigo_iso, tc_compra, tc_venta, tc_interbancario_comprador, tc_interbancario_vendedor, notas }) {
  const { data, error } = await supabase.functions.invoke('tipo-cambio-set-manual', {
    body: { fecha, codigo_iso, tc_compra, tc_venta, tc_interbancario_comprador, tc_interbancario_vendedor, notas },
  })
  if (error) throw new Error(error.message || 'set tc manual')
  if (data?.error) throw new Error(data.error)
  return data
}

/** Borrar un TC específico (fecha + ISO). Lo decide el operador. */
export async function borrarTipoCambio(fecha, codigo_iso) {
  const cod = MONEDAS_BCU[codigo_iso]
  if (!cod) throw new Error('Moneda no soportada: ' + codigo_iso)
  const { error } = await supabase
    .from('tipos_cambio_bcu')
    .delete()
    .eq('fecha', fecha)
    .eq('cod_moneda_bcu', cod)
  if (error) throw error
}

/** Disparar fetch BCU on-demand */
export async function refrescarBCU({ fecha, monedas = ['USD'] } = {}) {
  const { data, error } = await supabase.functions.invoke('bcu-fetch-cotizacion', {
    body: { fecha, monedas },
  })
  if (error) throw new Error(error.message || 'bcu fetch')
  return data
}

// ──────────── KPIs FINANCIEROS ────────────

export async function getKPIs(fecha = null) {
  const { data, error } = await supabase.rpc('kpis_financieros', fecha ? { p_fecha: fecha } : {})
  if (error) { console.error('kpis_financieros:', error); return [] }
  return data || []
}

// ──────────── CATÁLOGOS ────────────

export const CATEGORIAS_GASTO = [
  { value: 'gastos_personal',        label: 'Gastos de personal',          desc: 'Sueldos, BPS, FRL, aportes patronales' },
  { value: 'gastos_administrativos', label: 'Gastos administrativos',      desc: 'Seguros, sistemas (Biller, Zeta), servicios profesionales' },
  { value: 'gastos_comerciales',     label: 'Gastos comerciales',          desc: 'Marketing, comisiones procesador (Handy), publicidad' },
  { value: 'gastos_operativos',      label: 'Gastos operativos',           desc: 'Combustible, mantenimiento vehículo, logística' },
  { value: 'gastos_financieros',     label: 'Gastos financieros',          desc: 'Intereses, comisiones bancarias' },
  { value: 'impuestos_deducibles',   label: 'Impuestos deducibles',        desc: 'BPS patronal, FRL — bajan IRAE' },
  { value: 'anticipos_a_cuenta',     label: 'Anticipos a cuenta (activo)', desc: 'Anticipo IRAE — NO es gasto, es activo' },
  { value: 'gastos_no_deducibles',   label: 'Gastos no deducibles',        desc: 'Multas, accidentales no documentados' },
]

export const TASAS_IVA = [
  { value: 0,  label: '0% (exento / fuera del IVA)' },
  { value: 10, label: '10% (tasa reducida)' },
  { value: 22, label: '22% (tasa básica)' },
]

export const TIPOS_CUENTA = [
  { value: 'activo',     label: 'Activo' },
  { value: 'pasivo',     label: 'Pasivo' },
  { value: 'patrimonio', label: 'Patrimonio' },
  { value: 'ingreso',    label: 'Ingreso' },
  { value: 'egreso',     label: 'Egreso' },
]

// ──────────── HELPERS DE CÁLCULO ────────────

export function calcularConIva(montoNeto, tasaIva) {
  return Math.round(Number(montoNeto || 0) * (1 + Number(tasaIva || 0) / 100))
}

export function calcularCostoNetoReal(g) {
  const conIva = Number(g.monto_neto) * (1 + Number(g.tasa_iva) / 100)
  const descConIva = Number(g.monto_descuento || 0) * (1 + Number(g.tasa_iva) / 100)
  return Math.round(conIva - descConIva)
}

export function categoriaLabel(value) {
  return CATEGORIAS_GASTO.find(c => c.value === value)?.label || value
}

export function tipoLabel(value) {
  return TIPOS_CUENTA.find(t => t.value === value)?.label || value
}
